import {GetDoc, GetDocs, CreateAccessor} from "mobx-graphlink";
import {GetNewLocalSessions} from "../../Utils/Bridge/Bridge_Preload.js";
import {RunInAction} from "web-vcore";
import {emptyArray, Timer} from "js-vextensions";
import {observable} from "mobx";
import moment from "moment";
import {Session} from "./sessions/@Session.js";
import {MeID} from "./users.js";

export const GetSession = CreateAccessor((id: string|n): Session|n=>{
	//if (id == null) return null; // defensive

	const localEntryMatch = Array.from(localSessionMap.values()).find(a=>a.id == id);
	if (localEntryMatch) {
		return GetSessions(localEntryMatch.creator ?? MeID()).find(a=>a.id == id);
	}

	//return GetDoc({}, a=>a.sessions.get(id))?.Cast(EngineSessionInfo);
	//return GetDoc({}, a=>a.sessions.get(id!));

	// We want the session object to be the same (eg. for identity-check in DetailsPanel), regardless of if calling GetSession(id) or GetSessions(MeID()).
	// So, for now: we just extend the GetSessions accessor, and find the session in that array. (needed since mobx-graphlink has separate caches for "sessions" docs, versus "sessions (filtered by user-id)" docs)
	// (later, may change mobx-graphlink to always store/cache entries on the table-level, rather than table and table+filter levels)
	const ownSessions = GetSessions(MeID());
	return ownSessions.find(a=>a.id == id);
});

/** Returns the timeline-events for the given user. */
export const GetSessions = CreateAccessor((userID: string|n): Session[]=>{
	/*return GetDocs({
		queryOps: [new WhereOp("creator", "==", userID)],
	}, a=>a.sessions);*/
	return GetSessionsIntersectingRange(userID, 0);
});
export const GetOpenSession = CreateAccessor({ctx: 1}, function() {
	const sessions = MeID() ? GetSessions(MeID()) : emptyArray as Session[];
	return sessions.find(a=>a.id == this.store.main.timeline.sessions.selectedSession);
});

export const GetSessionsIntersectingRange = CreateAccessor((userID: string|n, rangeStart: number, rangeEnd?: number): Session[]=>{
	const webSessions = GetSessionsIntersectingRange_Web(userID, rangeStart, rangeEnd);
	const localSessions = GetSessionsIntersectingRange_Local(userID, rangeStart, rangeEnd);
	const result = localSessions.slice();
	for (const session of webSessions) {
		const localEntry = localSessions.find(a=>a.startTime == session.startTime);
		if (localEntry) {
			// if local-entry exists, use it instead of the web-entry (we just gotta pick one)

			/*const localEntry_new = Clone(localEntry);
			localEntry_new.VSet("_online", true);
			result[result.indexOf(localEntry)] = localEntry_new;*/

			// just mutate the object; we're not ruining anything (and this way we prevent detail-panel-eeg-processor from having to restart the processing)
			//session.VSet("_online", true, {prop: {enumerable: false}});
			localEntry._online = true; // mark local-entry as also having online data
			//result.push(localEntry); // already added at array declaration
		} else {
			/*const session_new = Clone(session);
			session_new.VSet("_online", true);
			result.push(session_new);*/

			// just mutate the object; we're not ruining anything (and this way we perhaps prevent some UIs refreshing without need)
			//session.VSet("_online", true, {prop: {enumerable: false}});
			session._online = true;
			result.push(session);
		}
	}
	return result.OrderBy(a=>a.startTime);
});

// local sessions (ie. local files accessed through electron interface)
// ==========

export const mainDataPathMarker = "*MainData*"; // we include "*", so that if substitution is missed, the path is invalid
export const sessionFolderName_startTimeFormatStr = "YYYY-MM-DD-HH-mm-ss";
/*export function GetSessionFolderPath(sessionStartTime: number) {
	return `${mainDataPathMarker}/Sessions/${moment(sessionStartTime).format(sessionFolderName_startTimeFormatStr)}`;
}*/
export function GetSessionFolderPath(sessionFolderName: string) {
	return `${mainDataPathMarker}/Sessions/${sessionFolderName}`;
}
export function GetSessionSubpath(sessionFolderName: string, subpath: string) {
	return `${GetSessionFolderPath(sessionFolderName)}/${subpath}`;
}

//export const localSessionMap = new ObservableMap<number, EngineSessionInfo>();
export const localSessionMap = observable.map<number, Session>({}, {deep: false}); // have shallow, so that entries in map are not proxies (ensuring no reference-mismatches)
export const UpdateLocalSessionsMap_callOnceTimer = new Timer(0, ()=>UpdateLocalSessionsMap(), 1);
// todo: probably make-so only the needed range is read
export function UpdateLocalSessionsMap(refreshAll = false, sessionsToRefresh_folderNames = [] as string[]) {
	RunInAction("UpdateLocalSessionMap", ()=>{
		const knownFolders = Array.from(localSessionMap.values()).map(a=>moment(a.startTime).format(sessionFolderName_startTimeFormatStr));
		const foldersToNotRefresh = refreshAll ? [] : knownFolders.Exclude(...sessionsToRefresh_folderNames);
		const newSessionInfos = GetNewLocalSessions(foldersToNotRefresh) as Session[];
		for (const session of newSessionInfos) {
			//session.VSet("_local", true, {prop: {enumerable: false}});
			session._local = true;

			//localSessionMap.set(session.id, session);
			localSessionMap.set(session.startTime, session); // store by start-time (not id), because id can change after uploading
		}
	});
}
export const GetSessionsIntersectingRange_Local = CreateAccessor((userID: string|n, rangeStart: number, rangeEnd?: number): Session[]=>{
	if (localSessionMap.size == 0 && UpdateLocalSessionsMap_callOnceTimer.startTime == null) {
		UpdateLocalSessionsMap_callOnceTimer.Start();
	}

	return Array.from(localSessionMap.values()).filter(session=>{
		//if (session.creator != userID) return false; // uncomment later, once multiple people using
		const inRange = session.endTime >= rangeStart && (rangeEnd == null || session.startTime < rangeEnd);
		return inRange;
	}).OrderBy(a=>a.startTime);
});

export const GetSessionsIntersectingRange_Web = CreateAccessor((userID: string|n, rangeStart: number, rangeEnd?: number): Session[]=>{
	const closedSessionsInRange = GetDocs({
		params: {filter: {
			creator: userID != null && {equalTo: userID},
			endTime: {greaterOrEqualTo: rangeStart}, // make sure session ended after range's start
			startTime: rangeEnd != null && {lessThan: rangeEnd}, // make sure session started prior to range's end
		}}
	}, a=>a.sessions);

	/*const ongoingSessions = GetDocs({
		queryOps: [new WhereOp("creator", "==", userID), new WhereOp("endTime", "==", null)],
	}, a=>a.sessions);
	const ongoingSessionsInRange = ongoingSessions.filter(session=>(rangeEnd == null || session.startTime < rangeEnd));*/

	const allSessionsInRange = closedSessionsInRange; //.concat(ongoingEventsInRange);
	return allSessionsInRange.OrderBy(a=>a.startTime);
});
/*#* Returns the sessions for the given user, which intersect with the time period specified. */
/*export const GetSessionsIntersectingRange_ByWeek = CreateAccessor((userID: string, rangeStart: number, rangeEnd?: number): EngineSessionInfo[]=>{
	const weekIDs = [TODO];
	Assert(weekIDs.length > 10, "Cannot get sessions by-week, for a period larger than 10 weeks.");

	const sessionsRoughlyInRange = GetDocs({
		queryOps: [new WhereOp("creator", "==", userID), new WhereOp("week", "in", weekIDs)],
	}, a=>a.sessions);
	const sessionsInRange = sessionsRoughlyInRange.filter(session=>{
		return session.endTime >= rangeStart // make sure event ended after range's start
			&& (rangeEnd == null || session.startTime < rangeEnd); // make sure event started prior to range's end
	});

	return sessionsInRange.OrderBy(a=>a.startTime);
});*/

export const GetTimelineEvent = CreateAccessor((id: string)=>{
	return GetDoc({}, a=>a.timelineEvents.get(id));
});

/*export const GetInProgressTimelineEvents = CreateAccessor((timelineID: string)=>{
	return GetDocs({
		queryOps: [
			new WhereOp("timeline", "==", timelineID),
			new WhereOp("endTime", "<", ""), // `if value < ""` means "if value is null"
		],
	}, a=>a.timelineEvents).OrderBy(a=>a.startTime);
});*/